import { useCallback, useEffect, useRef, useState } from 'react';
import { track } from './track';

export function useAudioController(surah, reciter) {
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const queueIndexRef = useRef(0);
  const [activeAyah, setActiveAyah] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [queueProgress, setQueueProgress] = useState({ current: 0, total: 0, label: '' });
  const [error, setError] = useState('');

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    queueRef.current = [];
    queueIndexRef.current = 0;
    setActiveAyah(null);
    setIsPlaying(false);
    setQueueProgress({ current: 0, total: 0, label: '' });
  }, []);

  useEffect(() => stop, [stop, surah.number, reciter]);

  const playQueueItem = useCallback((index) => {
    const audio = audioRef.current;
    const item = queueRef.current[index];
    if (!audio || !item) return;
    queueIndexRef.current = index;
    audio.src = item.url;
    setActiveAyah(item.numberInSurah);
    setQueueProgress((current) => ({ ...current, current: index + 1 }));
    setError('');
    const promise = audio.play();
    if (promise) {
      promise.catch(() => {
        setError('De audio kon niet worden gestart. Controleer je internetverbinding en probeer opnieuw.');
        setIsPlaying(false);
      });
    }
  }, []);

  const playSequence = useCallback((ayahNumbers, label = 'Oefenreeks') => {
    const items = ayahNumbers.map((numberInSurah) => {
      const ayah = surah.ayahs[numberInSurah - 1];
      return { numberInSurah, url: ayah.audio[reciter] };
    }).filter(Boolean);
    if (!items.length) return;
    queueRef.current = items;
    queueIndexRef.current = 0;
    setQueueProgress({ current: 1, total: items.length, label });
    setIsPlaying(true);
    track('audio_play', { label: `${surah.number} ${surah.latinName}`, value: items.length });
    playQueueItem(0);
  }, [playQueueItem, reciter, surah.ayahs, surah.number, surah.latinName]);

  const playAyah = useCallback((numberInSurah) => {
    if (activeAyah === numberInSurah && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    playSequence([numberInSurah], `Vers ${numberInSurah}`);
  }, [activeAyah, isPlaying, playSequence]);

  const playSurah = useCallback(() => {
    playSequence(surah.ayahs.map((ayah) => ayah.numberInSurah), `Soera ${surah.latinName}`);
  }, [playSequence, surah.ayahs, surah.latinName]);

  const togglePause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !activeAyah) return;
    if (audio.paused) {
      audio.play().then(() => setIsPlaying(true)).catch(() => setError('De audio kon niet worden hervat.'));
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, [activeAyah]);

  const handleEnded = useCallback(() => {
    const nextIndex = queueIndexRef.current + 1;
    if (nextIndex < queueRef.current.length) {
      playQueueItem(nextIndex);
      return;
    }
    // alleen volledige reeksen (meer dan 1 vers) als 'afgerond' tellen
    if (queueRef.current.length > 1) {
      track('audio_complete', { label: `${surah.number} ${surah.latinName}`, value: queueRef.current.length });
    }
    setIsPlaying(false);
    setActiveAyah(null);
    setQueueProgress((current) => ({ ...current, current: current.total }));
  }, [playQueueItem, surah.number, surah.latinName]);

  return {
    audioRef,
    activeAyah,
    isPlaying,
    queueProgress,
    error,
    playAyah,
    playSurah,
    playSequence,
    togglePause,
    stop,
    audioProps: {
      onEnded: handleEnded,
      onPlay: () => setIsPlaying(true),
      onPause: () => setIsPlaying(false),
      onError: () => setError('Dit audiobestand is tijdelijk niet bereikbaar.'),
    },
  };
}
